import React, { useEffect, useRef, useState, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { SpeedDial, SpeedDialAction } from "@material-ui/lab";
import { Zoom } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import StorefrontIcon from "@material-ui/icons/Storefront";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import BarChartIcon from "@material-ui/icons/BarChart";
import PaymentIcon from "@material-ui/icons/Payment";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AssignmentReturnIcon from "@material-ui/icons/AssignmentReturn";
import TuneIcon from "@material-ui/icons/Tune";
import AddShoppingCartIcon from "@material-ui/icons/AddShoppingCart";
import HistoryIcon from "@material-ui/icons/History";
import { useLocation, useHistory } from "react-router-dom";
import AddClient from "../Invoice/AddClient";
import AddSupplier from "../Suppliers/AddSupplier";
import MeetingForm from "../Meetings/MeetingForm";
import { AppContext } from "../../Context/AppContext";
import AddItem from "../Items/addItem";
import usePermissions from "../../utils/permissions";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import styles from "./Fab.module.css";

// Left open and forgotten shouldn't sit fanned out over the page indefinitely.
const AUTO_CLOSE_MS = 30000;

const useActionLabelStyles = makeStyles(() => ({
  tooltipLabel: {
    backgroundColor: "#23232e !important",
    color: "#ffffff !important",
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: "6px 14px !important",
    borderRadius: "999px !important",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.35) !important",
    whiteSpace: "nowrap",
  },
}));

// CHANGE:
// This tooltip is controlled only by the main FAB hover state.
// It is NOT attached to the SpeedDial/list wrapper.
const useFabHintStyles = makeStyles(() => ({
  hintBubble: {
    position: "absolute",
    left: "calc(100% + 12px)",
    bottom: 10,

    backgroundColor: "#23232e",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.8rem",
    lineHeight: 1,

    padding: "9px 13px",
    borderRadius: "8px",

    whiteSpace: "nowrap",
    pointerEvents: "none",

    opacity: 0,
    visibility: "hidden",
    transform: "translateX(-4px)",

    transition:
      "opacity 120ms ease-out, transform 120ms ease-out, visibility 0s linear 120ms",

    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.35)",

    zIndex: 9999,

    "&::before": {
      content: '""',
      position: "absolute",
      left: "-5px",
      top: "50%",
      transform: "translateY(-50%)",
      borderTop: "5px solid transparent",
      borderBottom: "5px solid transparent",
      borderRight: "5px solid #23232e",
    },
  },

  // CHANGE:
  // Tooltip becomes visible only while the MAIN FAB is hovered.
  hintVisible: {
    opacity: 1,
    visibility: "visible",
    transform: "translateX(0)",
    transition:
      "opacity 120ms ease-out, transform 120ms ease-out, visibility 0s linear 0s",
  },
}));

const ActionLabel = ({ children, onClick }) => (
  <span
    onClick={onClick}
    style={{
      cursor: "pointer",
      display: "block",
      margin: "-6px -14px",
      padding: "6px 14px",
    }}
  >
    {children}
  </span>
);

const FabButton = () => {
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const actionLabelClasses = useActionLabelStyles();
  const fabHintClasses = useFabHintStyles();

  const fabWrapperRef = useRef(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isPosScreen =
    location.pathname.includes("/PosMainScreen") ||
    location.pathname.startsWith("/edit/invoice");
  // SuperAdmin's Global Settings > Goods Return entitlement.
  const effectiveProfile = useEffectiveProfile();
  const goodsReturnEnabled = effectiveProfile?.goodsReturnEntitled !== false;

  const mainButtonStyles = {
    backgroundColor: "#ef7f1a",
    ...(isMobile && {
      width: 48,
      height: 48,
    }),
  };

  const [open, setOpen] = useState(false);
  const [openSupplier, setOpenSupplier] = useState(false);
  const [openItem, setOpenItem] = useState(false);
  const [dialOpen, setDialOpen] = useState(false);

  // Tracks hover on the main FAB for tooltip display.
  const [fabHovered, setFabHovered] = useState(false);

  const {
    showForm,
    setShowForm,
    stockAdjustmentShowForm,
    setStockAdjustmentShowForm,
  } = useContext(AppContext);

  // Reset UI state whenever route changes.
  useEffect(() => {
    setStockAdjustmentShowForm(false);
    setShowForm(false);
    setOpen(false);
    setOpenItem(false);
    setOpenSupplier(false);
    setDialOpen(false);
    setFabHovered(false);
  }, [location, setShowForm, setStockAdjustmentShowForm]);

  // Main FAB hover handling for tooltip.
  const handleFabMouseEnter = () => {
    if (!dialOpen) {
      setFabHovered(true);
    }
  };

  const handleFabMouseLeave = () => {
    setFabHovered(false);
  };

  // Auto-close the speed dial after 30 seconds.
  useEffect(() => {
    if (!dialOpen) return undefined;

    const timer = setTimeout(() => {
      setDialOpen(false);
    }, AUTO_CLOSE_MS);

    return () => clearTimeout(timer);
  }, [dialOpen]);

  // Close when clicking outside the dial.
  useEffect(() => {
    if (!dialOpen) return undefined;

    const handleOutsideClick = (e) => {
      if (
        fabWrapperRef.current &&
        !fabWrapperRef.current.contains(e.target)
      ) {
        setDialOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick, true);

    return () => {
      document.removeEventListener("click", handleOutsideClick, true);
    };
  }, [dialOpen]);

  const {
    isAdmin,
    canReadPage,
    canCreatePage,
    canDownloadReports,
    permissionsLoading,
  } = usePermissions();

  const handleCloseForm = () => {
    setShowForm(false);
  };

  const navigateTo = (path) => {
    setDialOpen(false);

    // CHANGE:
    // Hide tooltip when navigation starts.
    setFabHovered(false);

    history.push(path);
  };

  const actionFabProps = {
    style: {
      backgroundColor: "#ef7f1a",
      margin: "10px 8px",
    },
  };

  // Keep SpeedDial click-only.
  const handleDialOpen = (event, reason) => {
    if (reason === "toggle") {
      setDialOpen(true);

      // CHANGE:
      // Hide "Click to open" once the dial is opened.
      setFabHovered(false);
    }
  };

  const handleDialClose = (event, reason) => {
    if (reason === "toggle" || reason === "escapeKeyDown") {
      setDialOpen(false);
    }
  };

  return (
    <div ref={fabWrapperRef} className={styles.floatingFab}>
      <Dialog
        open={showForm}
        onClose={handleCloseForm}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? 0 : 12,
          },
        }}
      >
        <DialogContent className="slim-scrollbar">
          <MeetingForm onClose={handleCloseForm} />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseForm}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <AddClient
        setOpen={setOpen}
        open={open}
      />

      <AddSupplier
        setOpen={setOpenSupplier}
        open={openSupplier}
      />

      <AddItem
        setOpen={setOpenItem}
        open={openItem}
      />

      {!stockAdjustmentShowForm &&
        !open &&
        !openItem &&
        !openSupplier && (
          <div
            className="fab-root-container"
            style={{
              position: "fixed",
              bottom: isPosScreen
                ? isMobile
                  ? "130px"
                  : "128px"
                : isMobile
                ? "70px"
                : "85px",
              left: isMobile ? "16px" : "96px",
              zIndex: 1001,
            }}
          >
            <SpeedDial
              ariaLabel="Quick actions"

              icon={
                <Zoom
                  in
                  appear
                  key={dialOpen ? "close" : "menu"}
                  timeout={180}
                >
                  {dialOpen ? (
                    <CloseIcon
                      style={{
                        fontSize: isMobile
                          ? "1.2rem"
                          : "1.5rem",
                      }}
                    />
                  ) : (
                    <MenuIcon
                      style={{
                        fontSize: isMobile
                          ? "1.2rem"
                          : "1.5rem",
                      }}
                    />
                  )}
                </Zoom>
              }

              open={dialOpen}
              onOpen={handleDialOpen}
              onClose={handleDialClose}
              direction="up"

              // CHANGE:
              // IMPORTANT:
              // These hover events are attached directly to the
              // actual MAIN FAB button.
              //
              // The expanded SpeedDial action/list area does not
              // trigger these handlers.
              FabProps={{
                style: mainButtonStyles,
                onMouseEnter: handleFabMouseEnter,
                onMouseLeave: handleFabMouseLeave,
              }}
            >
              {(isAdmin() || canReadPage("Items")) && (
                <SpeedDialAction
                  icon={
                    <StorefrontIcon
                      style={{ color: "#ffffff" }}
                    />
                  }
                  tooltipTitle={
                    <ActionLabel
                      onClick={() =>
                        navigateTo("/ItemsList")
                      }
                    >
                      Items
                    </ActionLabel>
                  }
                  tooltipOpen
                  classes={{
                    staticTooltipLabel:
                      actionLabelClasses.tooltipLabel,
                  }}
                  tooltipPlacement="right"
                  onClick={() =>
                    navigateTo("/ItemsList")
                  }
                  FabProps={actionFabProps}
                />
              )}

              {isAdmin() && effectiveProfile?.activityLogsVisible !== false && (
                <SpeedDialAction
                  icon={
                    <HistoryIcon
                      style={{ color: "#ffffff" }}
                    />
                  }
                  tooltipTitle={
                    <ActionLabel
                      onClick={() =>
                        navigateTo("/program-logs")
                      }
                    >
                      Activity Logs
                    </ActionLabel>
                  }
                  tooltipOpen
                  classes={{
                    staticTooltipLabel:
                      actionLabelClasses.tooltipLabel,
                  }}
                  tooltipPlacement="right"
                  onClick={() =>
                    navigateTo("/program-logs")
                  }
                  FabProps={actionFabProps}
                />
              )}

              {(isAdmin() || canReadPage("Units")) && (
                <SpeedDialAction
                  icon={
                    <EqualizerIcon
                      style={{ color: "#ffffff" }}
                    />
                  }
                  tooltipTitle={
                    <ActionLabel
                      onClick={() =>
                        navigateTo("/stockDetails")
                      }
                    >
                      Stock Summary
                    </ActionLabel>
                  }
                  tooltipOpen
                  classes={{
                    staticTooltipLabel:
                      actionLabelClasses.tooltipLabel,
                  }}
                  tooltipPlacement="right"
                  onClick={() =>
                    navigateTo("/stockDetails")
                  }
                  FabProps={actionFabProps}
                />
              )}

              {(isAdmin() || canDownloadReports()) && (
                <SpeedDialAction
                  icon={
                    <BarChartIcon
                      style={{ color: "#ffffff" }}
                    />
                  }
                  tooltipTitle={
                    <ActionLabel
                      onClick={() =>
                        navigateTo("/reports")
                      }
                    >
                      Reports
                    </ActionLabel>
                  }
                  tooltipOpen
                  classes={{
                    staticTooltipLabel:
                      actionLabelClasses.tooltipLabel,
                  }}
                  tooltipPlacement="right"
                  onClick={() =>
                    navigateTo("/reports")
                  }
                  FabProps={actionFabProps}
                />
              )}

              {(isAdmin() ||
                canReadPage("Payments & Receipts")) && (
                <SpeedDialAction
                  icon={
                    <PaymentIcon
                      style={{ color: "#ffffff" }}
                    />
                  }
                  tooltipTitle={
                    <ActionLabel
                      onClick={() =>
                        navigateTo("/meetings")
                      }
                    >
                      Payments/Receipts
                    </ActionLabel>
                  }
                  tooltipOpen
                  classes={{
                    staticTooltipLabel:
                      actionLabelClasses.tooltipLabel,
                  }}
                  tooltipPlacement="right"
                  onClick={() =>
                    navigateTo("/meetings")
                  }
                  FabProps={actionFabProps}
                />
              )}

              {(isAdmin() || canReadPage("Invoices")) && (
                <SpeedDialAction
                  icon={
                    <ReceiptIcon
                      style={{ color: "#ffffff" }}
                    />
                  }
                  tooltipTitle={
                    <ActionLabel
                      onClick={() =>
                        navigateTo("/invoices")
                      }
                    >
                      Invoices
                    </ActionLabel>
                  }
                  tooltipOpen
                  classes={{
                    staticTooltipLabel:
                      actionLabelClasses.tooltipLabel,
                  }}
                  tooltipPlacement="right"
                  onClick={() =>
                    navigateTo("/invoices")
                  }
                  FabProps={actionFabProps}
                />
              )}

              {(isAdmin() || canReadPage("Invoices")) && goodsReturnEnabled && (
                <SpeedDialAction
                  icon={
                    <AssignmentReturnIcon
                      style={{ color: "#ffffff" }}
                    />
                  }
                  tooltipTitle={
                    <ActionLabel
                      onClick={() =>
                        navigateTo("/goods-returns")
                      }
                    >
                      Goods Returns
                    </ActionLabel>
                  }
                  tooltipOpen
                  classes={{
                    staticTooltipLabel:
                      actionLabelClasses.tooltipLabel,
                  }}
                  tooltipPlacement="right"
                  onClick={() =>
                    navigateTo("/goods-returns")
                  }
                  FabProps={actionFabProps}
                />
              )}

              {location.pathname !== "/invoice" &&
                (isAdmin() ||
                  canReadPage("Stock Adjustment")) && (
                  <SpeedDialAction
                    icon={
                      <TuneIcon
                        style={{ color: "#ffffff" }}
                      />
                    }
                    tooltipTitle={
                      <ActionLabel
                        onClick={() => {
                          if (
                            permissionsLoading ||
                            (!isAdmin() &&
                              !canCreatePage(
                                "Stock Adjustment"
                              ))
                          ) {
                            return;
                          }

                          navigateTo("/stockAdjustment");
                        }}
                      >
                        Adjust Stock
                      </ActionLabel>
                    }
                    tooltipOpen
                    classes={{
                      staticTooltipLabel:
                        actionLabelClasses.tooltipLabel,
                    }}
                    tooltipPlacement="right"
                    onClick={() =>
                      navigateTo("/stockAdjustment")
                    }
                    FabProps={{
                      disabled:
                        permissionsLoading ||
                        (!isAdmin() &&
                          !canCreatePage(
                            "Stock Adjustment"
                          )),
                      style: actionFabProps.style,
                    }}
                  />
                )}

              {location.pathname !== "/invoice" &&
                (isAdmin() ||
                  canReadPage("Purchase")) && (
                  <SpeedDialAction
                    icon={
                      <AddShoppingCartIcon
                        style={{ color: "#ffffff" }}
                      />
                    }
                    tooltipTitle={
                      <ActionLabel
                        onClick={() => {
                          if (
                            permissionsLoading ||
                            (!isAdmin() &&
                              !canCreatePage("Purchase"))
                          ) {
                            return;
                          }

                          navigateTo("/purchase");
                        }}
                      >
                        Add Purchase
                      </ActionLabel>
                    }
                    tooltipOpen
                    classes={{
                      staticTooltipLabel:
                        actionLabelClasses.tooltipLabel,
                    }}
                    tooltipPlacement="right"
                    onClick={() =>
                      navigateTo("/purchase")
                    }
                    FabProps={{
                      disabled:
                        permissionsLoading ||
                        (!isAdmin() &&
                          !canCreatePage("Purchase")),
                      style: actionFabProps.style,
                    }}
                  />
                )}
            </SpeedDial>

            {/* CHANGE:
                Tooltip is outside SpeedDial but remains inside the
                fixed anchor. It is shown ONLY when the main FAB is
                hovered.
                
                Hovering Items, Stock Summary, Reports, etc. will NOT
                show "Click to open".
            */}
            {!dialOpen && fabHovered && (
              <span
                className={`${fabHintClasses.hintBubble} ${
                  fabHintClasses.hintVisible
                }`}
              >
                Click to open
              </span>
            )}
          </div>
        )}
    </div>
  );
};

export default FabButton;